import styled, { createGlobalStyle } from 'styled-components';
import { PrimaryButton, IconBadge } from '../../ui/common';

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;

  @media(max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

// Ikona hero strony — wspólna plakietka z ui/common.js (IconBadge) zamiast lokalnej pełnej
// niebieskiej wypełnionej ikony; ton "dusk" (zmierzch) sygnalizuje temat strony (czas/terminy),
// patrz komentarz przy ICON_TONES w ui/common.js.
export const TitleIcon = styled(IconBadge).attrs({ $size: 46, $tone: 'dusk' })``;

export const Title = styled.h1`
  margin: 0;
  font-size: 1.55rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--text);
  line-height: 1.2;

  @media(max-width: 768px) {
    font-size: 1.3rem;
  }
`;

export const TitleHint = styled.div`
  margin-top: 2px;
  font-size: 0.85rem;
  color: var(--text-muted);

  @media(max-width: 768px) {
    display: none;
  }
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  @media(max-width: 768px) {
    & > * {
      flex: 1;
      justify-content: center;
    }
  }
`;

export const TabNavigation = styled.div`
  display: flex;
  gap: 2px;
  margin-bottom: 24px;
  border-bottom: 2px solid var(--input-border);

  @media (max-width: 540px) {
    flex-wrap: wrap;
  }
`;

export const TabButton = styled.button`
  position: relative;
  padding: 10px 16px 12px;
  border: none;
  background: transparent;
  color: ${props => props.active ? 'var(--primary)' : 'var(--text-muted)'};
  font-weight: 600;
  font-size: 0.92rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  transition: color 0.15s ease;

  &::after {
    content: '';
    position: absolute;
    left: 10px;
    right: 10px;
    bottom: -2px;
    height: 2px;
    border-radius: 2px 2px 0 0;
    background: ${props => props.active ? 'var(--primary)' : 'transparent'};
    transition: background 0.15s ease;
  }

  &:hover {
    color: ${props => props.active ? 'var(--primary)' : 'var(--text)'};
  }

  /* Cztery zakładki z ikonami nie mieszczą się na telefonie: nazwa i licznik zostają, a gdy i tego
     za dużo, zakładki przechodzą do drugiego wiersza. Bez zawijania tekst nachodził na sąsiada. */
  @media (max-width: 540px) {
    flex: 1 0 auto;
    justify-content: center;
    padding: 10px 6px 12px;
    font-size: 0.8rem;
    gap: 5px;

    svg {
      display: none;
    }
  }

  @media (max-width: 380px) {
    font-size: 0.75rem;

    svg {
      display: none;
    }
  }
`;

export const TabCount = styled.span`
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  font-weight: 700;
  background: ${props => props.active ? 'rgba(59, 130, 246, 0.14)' : 'var(--table-row-hover)'};
  color: inherit;
`;

export const MainContent = styled.div`
  display: grid;
  gap: 24px;
  
  @media(min-width: 1200px) {
    grid-template-columns: ${props => props.showSidebar ? '2fr 1fr' : '1fr'};
  }
`;

export const CompactCalendar = styled.div`
  background: var(--card-bg);
  border-radius: 16px;
  padding: 20px 20px 14px;
  border: 1px solid var(--input-border);

  .react-calendar {
    width: 100%;
    background: transparent;
    border: none;
    font-family: inherit;
    color: var(--text);
  }

  /* Pasek nawigacji: miesiąc po lewej, strzałki po prawej */
  .react-calendar__navigation {
    display: flex;
    align-items: center;
    gap: 2px;
    height: auto;
    margin-bottom: 12px;
  }

  .react-calendar__navigation button {
    background: transparent;
    border: none;
    border-radius: 8px;
    color: var(--text-muted);
    font-weight: 600;
    min-width: 34px;
    height: 34px;
    font-size: 1.05rem;
    transition: background 0.15s ease, color 0.15s ease;
  }

  /* Palcem 34px to za malo (strzalki miesiaca na iPadzie, 27.09.2026). Ta regula ma klase,
     wiec wygrywa z globalnym min-width 44px dla przyciskow, stad powtorzenie tutaj. */
  @media (max-width: 768px), (hover: none) and (pointer: coarse) {
    .react-calendar__navigation button {
      min-width: 44px;
      height: 44px;
    }
  }

  .react-calendar__navigation button:enabled:hover,
  .react-calendar__navigation button:enabled:focus {
    background: var(--table-row-hover);
    color: var(--text);
  }

  .react-calendar__navigation button:disabled {
    background: transparent;
    opacity: 0.4;
  }

  .react-calendar__navigation__label {
    order: -1;
    flex-grow: 0 !important;
    margin-right: auto;
    padding: 0 10px;
    font-size: 1.08rem;
    font-weight: 700;
    color: var(--text);
    text-transform: capitalize;
    letter-spacing: -0.01em;
  }

  .react-calendar__navigation__arrow {
    font-size: 1.3rem;
    line-height: 1;
  }

  /* Nagłówki dni tygodnia */
  .react-calendar__month-view__weekdays {
    text-transform: uppercase;
    /* bylo 0.68rem = 10.88px. Wersaliki w wyciszonym kolorze i z odstepem czytaja
       sie trudniej niz zwykly tekst tej samej wielkosci, wiec 12px to tu minimum. */
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    color: var(--text-muted);
  }

  .react-calendar__month-view__weekdays__weekday {
    padding: 8px 4px;
    text-align: center;
  }

  .react-calendar__month-view__weekdays__weekday abbr {
    text-decoration: none;
  }

  /* Kafelki dni — czysta siatka bez obramowań */
  .react-calendar__tile {
    background: transparent;
    border: none;
    border-radius: 10px;
    color: var(--text);
    transition: background 0.15s ease, box-shadow 0.15s ease;
    padding: 6px;
    /* U.10: 100px (było 92) mieści 3 tory rezerwacji + pasek „+N więcej" bez nachodzenia na numer dnia */
    height: 100px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: stretch;
    position: relative;
    overflow: hidden;
  }

  /* Krąg z numerem dnia — TYLKO widok miesiąca. W widoku roku/dekady/wieku
     react-calendar owija w ten sam <abbr> całą nazwę miesiąca/roku (YearView/Month.js
     formatAbbr), więc bez tego zawężenia selektora "Wrzesień" wpychało się w 26px
     kółko, łamało w kilka linii i było ucinane przez overflow:hidden kafelka. */
  .react-calendar__month-view__days .react-calendar__tile abbr {
    width: 26px;
    height: 26px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    align-self: flex-start;
    border-radius: 50%;
    font-size: 0.82rem;
    font-weight: 600;
    line-height: 1;
  }

  .react-calendar__year-view .react-calendar__tile abbr,
  .react-calendar__decade-view .react-calendar__tile abbr,
  .react-calendar__century-view .react-calendar__tile abbr {
    width: auto;
    height: auto;
    display: inline-block;
    text-decoration: none;
    white-space: nowrap;
  }

  .react-calendar__tile:enabled:hover {
    background: var(--table-row-hover);
  }

  /* Weekendy (sobota i niedziela) - tylko czerwony tekst */
  .react-calendar__month-view__days__day:nth-of-type(7n),
  .react-calendar__month-view__days__day:nth-of-type(7n-1) {
    color: var(--danger) !important;
    font-weight: 600;
  }

  /* Dni z poprzedniego/następnego miesiąca */
  .react-calendar__month-view__days__day--neighboringMonth {
    background: transparent !important;
    color: var(--text-muted) !important;
    opacity: 0.45;

    &:hover {
      opacity: 0.7;
    }

    /* Weekendy z innych miesięcy - też czerwone ale przygaszone */
    &:nth-of-type(7n),
    &:nth-of-type(7n-1) {
      color: rgba(220, 38, 38, 0.6) !important;
    }
  }

  /* Wybrany dzień — delikatne tło + obrys */
  .react-calendar__tile--active {
    background: rgba(59, 130, 246, 0.08) !important;
    box-shadow: inset 0 0 0 2px var(--primary);
  }

  /* Dzisiaj — wypełnione kółko na numerze dnia */
  .react-calendar__tile--now {
    background: transparent;
  }

  .react-calendar__tile--now abbr {
    background: var(--primary);
    color: var(--primary-contrast);
    font-weight: 700;
    box-shadow: var(--shadow-primary);
  }

  /* Bieżący miesiąc/rok/dekada w widoku roku/dekady/wieku — ten sam abbr co dziś w
     widoku miesiąca, ale tam tekst jest jednym znakiem w kółku, a tu całym słowem
     ("wrzesień"), więc kółko trzeba zamienić na pigułkę z paddingiem, inaczej tło
     oblepia litery na styk i wygląda jak kwadratowe zaznaczenie tekstu. */
  .react-calendar__year-view .react-calendar__tile--now abbr,
  .react-calendar__decade-view .react-calendar__tile--now abbr,
  .react-calendar__century-view .react-calendar__tile--now abbr {
    padding: 6px 14px;
    border-radius: 999px;
  }

  /* Widok roku / dekady — wyśrodkowane kafelki */
  .react-calendar__year-view .react-calendar__tile,
  .react-calendar__decade-view .react-calendar__tile,
  .react-calendar__century-view .react-calendar__tile {
    height: 64px;
    align-items: center;
    justify-content: center;
    font-weight: 600;
  }

  /* Paski rezerwacji — etykiety z nazwiskami jak w profesjonalnych kalendarzach */
  .reservation-bars-container {
    position: absolute;
    bottom: 4px;
    left: 4px;
    right: 4px;
    display: flex;
    flex-direction: column;
    gap: 2px;
    z-index: 3;
  }

  .reservation-bar {
    display: block;
    height: 17px;
    line-height: 17px;
    border-radius: 4px;
    padding: 0 6px;
    font-size: 0.64rem;
    font-weight: 600;
    color: white;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Ciągłość pasków między sąsiednimi dniami. Pasek dochodzi do krawędzi kafelka (kontener ma
     4px wcięcia), a kafelek NIE może go przycinać: przy overflow hidden zaokrąglone rogi kafelka
     (border-radius 10px) wycinały z paska ząbki na granicy każdego dnia. react-calendar wpisuje
     overflow hidden w atrybut style kafelka, stąd !important. Tylko widok miesiąca: w widoku roku
     i dekady przycinanie zostaje. */
  .react-calendar__month-view__days .react-calendar__tile {
    overflow: visible !important;
  }

  /* Kontynuacja zachodzi 1px na poprzedni dzień: szerokość kolumny to 14,2857%, więc granice
     kafelków wypadają na ułamkach piksela i bez zakładki potrafi prześwitywać jasna linia. */
  .reservation-bar:not(.start) {
    margin-left: -5px;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  .reservation-bar:not(.end) {
    margin-right: -4px;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  .reservation-bar.start {
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;
  }

  .reservation-bar.end {
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
  }

  /* Pusty tor — niewidoczny spacer utrzymujący wyrównanie pasków między dniami (U.10) */
  .reservation-bar.empty {
    background: transparent;
    pointer-events: none;
  }

  /* Pasek "więcej rezerwacji" */
  .reservation-bar.more {
    background: transparent;
    color: var(--text-muted);
    font-weight: 700;
    height: 15px;
    line-height: 15px;
    margin: 0 !important;
    border-radius: 4px;
    cursor: pointer;

    &:hover {
      color: var(--text);
    }
  }

  /* Licznik rezerwacji na dniu */
  .reservation-count {
    position: absolute;
    top: 6px;
    right: 6px;
    background: var(--table-row-hover);
    color: var(--text-muted);
    font-size: 0.62rem;
    font-weight: 700;
    min-width: 17px;
    height: 17px;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 4px;
    z-index: 4;
  }

  /* Dark theme - weekendy jaśniejsze czerwone */
  [data-theme='dark'] & {
    .react-calendar__month-view__days__day:nth-of-type(7n),
    .react-calendar__month-view__days__day:nth-of-type(7n-1) {
      color: var(--danger) !important;
    }

    .react-calendar__month-view__days__day--neighboringMonth {
      &:nth-of-type(7n),
      &:nth-of-type(7n-1) {
        /* Przyciemniony token zamiast osobnego hexu: dzien z sasiedniego miesiaca + weekend to
           nakladka dwoch stanow (przygaszenie + kolor ostrzegawczy), stad token z domieszka
           przezroczystosci zamiast osobnej stalej barwy. */
        color: color-mix(in srgb, var(--danger) 60%, transparent) !important;
      }
    }

    .react-calendar__tile--active {
      background: rgba(59, 130, 246, 0.16) !important;
    }
  }

  @media (max-width: 768px) {
    padding: 12px;
    .react-calendar__tile {
      height: 58px;
      padding: 4px;
    }
    .react-calendar__month-view__days .react-calendar__tile abbr {
      width: 22px;
      height: 22px;
      font-size: 0.75rem;
    }
    .reservation-bar {
      height: 5px;
      line-height: 5px;
      padding: 0;
      font-size: 0;
      border-radius: 3px;
    }
    .reservation-bar.more {
      height: 12px;
      line-height: 12px;
      font-size: 0.7rem; /* bylo 0.55rem = 8.8px, ponizej progu czytelnosci */
    }

    .reservation-count {
      font-size: 0.7rem; /* bylo 0.55rem = 8.8px, ponizej progu czytelnosci */
      min-width: 14px;
      height: 14px;
      top: 4px;
      right: 4px;
    }
    /* Strzalki maja tu 44px z reguly dotyku wyzej. Dawniej stala tu druga regula z 32px, ktora
       wygrywala kolejnoscia i odbierala telefonom poprawke 44px (przeglad 28.09.2026). */
    .react-calendar__navigation__label {
      font-size: 0.95rem;
    }
  }

  /* Waski telefon: bez podwojnych strzalek (skok o rok). Cztery strzalki po 44px zabieraly
     tytulowi miejsce, wiec polska nazwa miesiaca lamala sie na dwa wiersze, a przy 320px karta
     wychodzila poza panel i przycisk z prawej byl uciety (przeglad 28.09.2026). Miesiac dalej
     zmieniaja strzalki pojedyncze, a rok mozna wybrac po stuknieciu w tytul. */
  @media (max-width: 480px) {
    .react-calendar__navigation__prev2-button,
    .react-calendar__navigation__next2-button {
      display: none;
    }
    /* Selektor z przyciskiem, bo regula przyciskow nawigacji (1.05rem) ma wieksza specyficznosc
       niz sama klasa etykiety: dawne 0.95rem z bloku do 768px przez to nigdy nie dzialalo.
       Przy 320px dluga nazwa („pazdziernik 2026”) miesci sie tak w jednym wierszu. */
    .react-calendar__navigation button.react-calendar__navigation__label {
      padding: 0 4px;
      font-size: 0.95rem;
    }
    /* Komorka naglowka dnia ma z react-calendar styl inline overflow hidden, a „NIEDZ.” jest przy
       320-375px szersze od kolumny (widac bylo NIED). Tekst jest wysrodkowany, wiec wystaje po
       kilka pikseli na boki, w odstep miedzy kolumnami i w padding karty. */
    .react-calendar__month-view__weekdays__weekday {
      overflow: visible !important;
      padding: 8px 0;
      letter-spacing: 0.02em;
    }
  }

  /* Napis „+N wiecej" chowamy do samego „+N" DOPIERO gdy przestaje sie miescic w kafelku dnia.
     Zmierzone: pelny napis potrzebuje 67px, a kafelek ma 47px przy 402, 57 przy 480, 65 przy 540
     i 74 dopiero przy 600. Stad prog 560, a nie 768: na tablecie slowo miescilo sie swobodnie
     i znikalo bez powodu. Pelne zdanie jest w podpowiedzi elementu, wiec nic nie ginie. */
  @media (max-width: 560px) {
    .reservation-bar.more .more-word {
      display: none;
    }
  }

  @media (max-width: 420px) {
    padding: 8px;
    /* Kafelek ma tu 33-41px szerokosci (siedem w wierszu od przegladu 28.09.2026), a licznik
       rezerwacji w rogu zaslanial druga cyfre dnia. Numer dnia stoi 2px blizej lewej krawedzi,
       licznik 1px od prawej krawedzi (nie wchodzi jeszcze w zaokraglony rog kafelka) — te dwie
       pozycje nie ruszaja sie dalej, bo obie maja swoje uzasadnienie z przegladu 28.09.2026.
       Zamiast tego SAM licznik jest tu mniejszy: przy stalej pozycji prawej krawedzi mniejsza
       szerokosc przesuwa jego LEWA krawedz w prawo, z dala od numeru. Poprzednia proba (14px,
       „do 4.7px przy 320px") zostawiala 0.8-2.0px zapasu — realny nachodzacy tekst na CI, ktore
       inaczej hintuje font niz przegladarka uzyta do pomiaru (round 3). Zmierzone tym razem
       Cypressem NA TEJ szerokosci, nie na oko: 8px/0.5rem zostawia >=4px zapasu dla kazdego
       dnia od 10 do 28 (test na prawdziwym getBoundingClientRect, nie tylko na wygladzie). */
    .react-calendar__tile {
      height: 48px;
      padding: 4px 2px;
    }
    .reservation-count {
      top: 1px;
      right: 1px;
      min-width: 8px;
      height: 8px;
      padding: 0 1px;
      font-size: 0.5rem;
    }
    .react-calendar__month-view__days .react-calendar__tile abbr {
      /* bylo 20px / 0.7rem = 11.2px. Numer dnia to glowna tresc siatki miesiaca,
         wiec nie moze byc najmniejszym tekstem na ekranie. Kolumna ma tu okolo
         50px, wiec kolko 22px nadal miesci sie z zapasem i siatka nie drgnie. */
      width: 22px;
      height: 22px;
      font-size: 0.8rem;
    }
  }
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 24px;
  right: 24px;
  background: transparent;
  border: none;
  font-size: 28px;
  color: var(--text-muted);
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 8px;
  transition: all 0.2s ease;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  
  &:hover {
    background: rgba(239, 68, 68, 0.1);
    color: var(--danger);
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  @media(max-width: 768px) {
    top: 16px;
    right: 16px;
  }
`;

export const SidePanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  /* Kolumna siatki nie może rosnąć do najszerszej karty: przy 320 px panel wychodził poza
     ekran i ucinał odznakę statusu oraz przycisk druku (złapane testem planu dnia, U.29). */
  min-width: 0;
`;

export const DayInfo = styled.div`
  background: var(--card-bg);
  border-radius: 16px;
  padding: 20px;
  border: 1px solid var(--input-border);
`;

export const DayHeader = styled.div`
  display: flex;
  /* U.29: w wąskim panelu przycisk druku planu schodzi pod datę, zamiast ją ściskać */
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--input-border);
`;

export const DayDateBox = styled.div`
  width: 54px;
  height: 54px;
  border-radius: 14px;
  background: var(--primary);
  color: var(--primary-contrast);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: var(--shadow-primary);

  .day-num {
    font-size: 1.35rem;
    font-weight: 800;
    line-height: 1;
  }

  .day-month {
    margin-top: 2px;
    /* bylo 0.6rem = 9.6px. Skrot miesiaca mowi, ktory to miesiac, wiec musi byc
       czytelny; wersaliki z odstepem czytaja sie jeszcze gorzej niz zwykly tekst. */
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    opacity: 0.9;
  }
`;

export const DayHeaderText = styled.div`
  min-width: 0;
  flex: 1 1 150px;

  .weekday {
    font-size: 1.05rem;
    font-weight: 700;
    color: var(--text);
    text-transform: capitalize;
  }

  .sub {
    margin-top: 2px;
    font-size: 0.83rem;
    color: var(--text-muted);
  }
`;

export const ReservationCard = styled.div`
  position: relative;
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 12px;
  padding: 14px 16px 14px 20px;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
  cursor: pointer;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: var(--res-accent, var(--primary));
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px var(--shadow-color);
    border-color: var(--primary);
  }
`;

export const ReservationHeader = styled.div`
  display: flex;
  /* W wąskiej karcie (telefon 320 px) odznaka statusu schodzi pod nazwisko, zamiast zostawiać
     z niego trzy litery: nazwisko jest tu ważniejsze niż status. */
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 6px 10px;
  margin-bottom: 10px;
`;

export const ClientName = styled.div`
  /* Element flex z nowrap ma minimalną szerokość równą całemu tekstowi, więc wielokropek
     niżej nie działał nigdy: długie nazwisko rozpychało kartę zamiast się skracać. */
  min-width: 0;
  font-weight: 700;
  color: var(--text);
  font-size: 0.98rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Znacznik zapytania z widgetu na karcie rezerwacji (069): „Do potwierdzenia”, „Do dobrania: 2”.
export const RequestTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border-radius: 6px;
  font-size: 0.72rem;
  font-weight: 700;
  color: ${(p) => (p.$ton === 'warn' ? 'var(--warning)' : 'var(--text-muted)')};
  background: ${(p) => (p.$ton === 'warn' ? 'color-mix(in srgb, var(--warning) 12%, transparent)' : 'var(--hover-bg)')};
`;

export const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  background: ${props => props.bg || 'var(--table-header-bg)'};
  color: ${props => props.color || 'var(--text-muted)'};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
`;

export const ReservationMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 14px;
  font-size: 0.83rem;
  color: var(--text-muted);

  .meta-item {
    display: inline-flex;
    align-items: center;
    gap: 5px;

    svg {
      opacity: 0.65;
      flex-shrink: 0;
    }
  }

  .meta-price {
    margin-left: auto;
    font-weight: 700;
    font-size: 0.92rem;
    color: var(--text);
  }
`;

// X.1: stan wydania i zwrotu na karcie rezerwacji („U klienta 2 z 3", „Sprzęt wrócił",
// „3 dni po terminie"). Ton wyłącznie z tokenów motywu.
export const HandoverChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  color: ${(p) => (p.$tone === 'warning' ? 'var(--warning)' : p.$tone === 'success' ? 'var(--success)' : 'var(--text-muted)')};
  background: ${(p) => (p.$tone === 'warning' ? 'var(--warning-bg)' : p.$tone === 'success' ? 'var(--success-bg)' : 'var(--hover-bg)')};
`;

// X.1: zakładka „Do zwrotu" — dwie grupy (spóźnione, termin dziś) jedna pod drugą.
export const ReturnsGroup = styled.section`
  & + & { margin-top: 24px; }
`;

export const ReturnsGroupTitle = styled.h3`
  margin: 0 0 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;
  font-weight: 700;
  color: ${(p) => (p.$tone === 'warning' ? 'var(--warning)' : 'var(--text)')};
`;

export const ReturnsHint = styled.p`
  margin: 0 0 16px;
  font-size: 0.83rem;
  color: var(--text-muted);
`;

export const FilterSection = styled.div`
  background: var(--card-bg);
  border-radius: 16px;
  padding: 18px 20px 20px;
  margin-bottom: 20px;
  border: 1px solid var(--input-border);
`;

export const FilterHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  font-size: 0.92rem;
  font-weight: 700;
  color: var(--text);

  svg {
    color: var(--text-muted);
  }
`;

export const ClearFiltersButton = styled.button`
  margin-left: auto;
  background: transparent;
  border: none;
  color: var(--text-muted);
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 10px;
  border-radius: 6px;
  transition: all 0.15s ease;

  &:hover {
    color: var(--danger);
    background: rgba(239, 68, 68, 0.08);
  }
`;

export const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 14px;
`;

export const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  & > label {
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-muted);
  }
`;

export const FilterInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  min-height: 44px;
  border: 2px solid var(--input-border);
  border-radius: 8px;
  font-size: 0.9rem;
  background: var(--input-bg);
  color: var(--text);
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
  }
`;

export const FilterDropdownButton = styled.button`
  width: 100%;
  padding: 10px 12px;
  min-height: 44px;
  border: 2px solid var(--input-border);
  border-radius: 8px;
  font-size: 0.9rem;
  background: var(--input-bg);
  color: var(--text);
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  box-sizing: border-box;

  &:hover { border-color: var(--primary); }
  &:focus-visible { outline: none; border-color: var(--primary); box-shadow: var(--focus-ring); }
  &[data-open="true"] { border-color: var(--primary); background: var(--card-bg); box-shadow: var(--focus-ring); }
`;

export const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 12px;

  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 48px 20px;
  color: var(--text-muted);

  .icon {
    width: 56px;
    height: 56px;
    margin: 0 auto 14px;
    border-radius: 16px;
    background: var(--table-row-hover);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 26px;

    /* Ikony SVG maja w icons.js stala szerokosc 16-20px, co w kafelku 56px wyglada drobno
       obok dawnych emotek (26px). Skalujemy je tutaj, zeby nie zmieniac rozmiaru ikon
       uzywanych w calej reszcie widoku. Kolor dziedziczy sie z EmptyState przez currentColor. */
    svg { width: 26px; height: 26px; }
  }

  .text {
    font-size: 0.95rem;
    font-weight: 600;
  }
`;

export const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 20px;
  padding: 8px;

  span {
    font-size: 0.88rem;
    font-weight: 600;
    color: var(--text-muted);
  }
`;

export const DropdownWrapper = styled.div`
  position: relative;
  width: 100%;
`;

export const FilterOptionsList = styled.ul`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  width: 100%;
  max-height: 200px;
  margin: 0;
  padding: 4px;
  list-style: none;
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 12px;
  box-shadow: 0 8px 32px var(--shadow-color);
  overflow-y: auto;
  z-index: var(--z-dropdown);
`;

export const FilterOptionItem = styled.li`
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--text);
  cursor: pointer;
  transition: background .15s, color .15s;

  .option-content {
    display: flex;
    align-items: center;
    gap: 8px;
    
    svg {
      color: var(--primary);
      flex-shrink: 0;
    }
    
    .option-label {
      flex: 1;
    }
  }

  &:hover {
    background: var(--table-row-hover);
  }
  
  &[data-selected="true"] {
    background: rgba(59,130,246,0.15);
    color: var(--primary);
    font-weight: 600;
  }
`;

export const PaymentDropdownButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid var(--input-border);
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 500;
  background: var(--input-bg);
  color: var(--text);
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  line-height: 1.2;
  transition: border-color .2s, box-shadow .2s, background .2s;
  gap: 8px;
  
  &:hover {
    border-color: var(--primary);
  }
  &:focus-visible {
    outline: none;
    border-color: var(--primary);
    background: var(--card-bg);
    box-shadow: var(--focus-ring);
  }
  &[data-open="true"] {
    border-color: var(--primary);
    background: var(--card-bg);
    box-shadow: var(--focus-ring);
    
    svg {
      color: var(--primary);
    }
  }
  
  svg {
    flex-shrink: 0;
    transition: color .2s;
    color: var(--text-muted);
  }
`;

export const FormInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid var(--input-border);
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 500;
  background: var(--input-bg);
  color: var(--text);
  box-sizing: border-box;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
  }
  
  &:disabled {
    background: var(--table-row-hover);
    color: var(--text-muted);
    cursor: not-allowed;
  }
  
  /* POPRAWIONE: ukrycie strzałek dla wszystkich input[type=number] */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
    display: none;
  }
  
  &[type='number'] {
    -moz-appearance: textfield;
    appearance: textfield;
  }
`;

export const FormTextarea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid var(--input-border);
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 500;
  background: var(--input-bg);
  color: var(--text);
  box-sizing: border-box;
  transition: all 0.2s ease;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
  }
`;

export const EquipmentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

export const EquipmentName = styled.span`
  font-weight: 500;
  color: var(--text);
`;

export const EquipmentMeta = styled.span`
  font-size: 0.85rem;
  color: var(--text-muted);
`;

export const EquipmentPrice = styled.span`
  font-weight: 600;
  color: var(--primary);
  margin-right: 12px;
`;

export const EquipmentCheckbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: var(--primary);
`;

export const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: var(--text-muted);
`;

export const RangeLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

export const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text);
`;

export const ClearRangeButton = styled.button`
  background: var(--input-bg);
  border: 1px solid var(--input-border);
  color: var(--text-muted);
  font-size: 0.65rem;
  padding: 6px 10px;
  border-radius: 8px;
  font-weight: 600;
  letter-spacing: .5px;
  cursor: pointer;
  transition: all .2s;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover:not(:disabled) {
    color: var(--danger);
    border-color: var(--danger);
    background: rgba(239,68,68,0.08);
  }
  &:disabled {
    opacity: .4;
    cursor: not-allowed;
  }
`;

export const DatepickerTheme = createGlobalStyle`
  .react-datepicker {
    width: 252px;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    background: var(--card-bg);
    border: 1px solid var(--input-border);
    border-radius: 14px;
    box-shadow: 0 10px 34px var(--shadow-color);
    overflow: hidden;
    padding: 0 0 6px 0;
  }
  .react-datepicker__month { margin: 4px 6px 0 6px; }

  .react-datepicker__day.past-day:not(.react-datepicker__day--selected):not(.react-datepicker__day--in-range) {
    color: var(--text-muted);
    opacity: 0.45;
    cursor: not-allowed;
    text-decoration: line-through;
  }

  .react-datepicker__triangle { display: none; }

  .react-datepicker-popper[data-placement^="bottom"] {
    margin-top: 6px;
    z-index: var(--z-popover) !important;
  }

  .react-datepicker__header {
    background: var(--primary);
    border-bottom: 1px solid rgba(255,255,255,0.15);
    padding-top: 6px;
    padding-bottom: 4px;
  }

  .react-datepicker__current-month {
    color: var(--primary-contrast);
    font-weight: 600;
    font-size: 0.90rem;
    letter-spacing: .3px;
  }

  .react-datepicker__day-names {
    margin-top: 2px;
  }

  .react-datepicker__day-name {
    color: rgba(255,255,255,0.85);
    font-weight: 500;
    width: 32px;
    line-height: 32px;
    margin: 1px;
    font-size: 0.65rem;
  }

  .react-datepicker__month {
    margin: 4px 6px 0 6px;
  }

  .react-datepicker__week {
    display: flex;
    justify-content: space-between;
  }

  .react-datepicker__day {
    width: 32px;
    height: 32px;
    line-height: 32px;
    margin: 1px;
    border-radius: 8px;
    font-weight: 500;
    font-size: 0.78rem;
    color: var(--text);
    transition: all .15s ease;
    outline: none;
  }

  .react-datepicker__day:hover {
    background: var(--table-row-hover);
    cursor: pointer;
  }

  .react-datepicker__day--outside-month {
    opacity: 0.30;
  }

  .react-datepicker__day--today:not(.react-datepicker__day--selected):not(.react-datepicker__day--in-range) {
    font-weight: 600;
    box-shadow: inset 0 0 0 2px rgba(59, 130, 246, 0.45);
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--keyboard-selected {
    background: var(--primary);
    color: var(--primary-contrast) !important;
    box-shadow: var(--shadow-primary);
  }

  .react-datepicker__day--in-range,
  .react-datepicker__day--in-selecting-range {
    background: rgba(59, 130, 246, 0.18);
    color: var(--text);
    border-radius: 8px;
  }

  .react-datepicker__day.weekend:not(.react-datepicker__day--selected):not(.react-datepicker__day--in-range):not(.react-datepicker__day--keyboard-selected) {
    color: var(--danger);
  }
  .react-datepicker__day.weekend:hover:not(.react-datepicker__day--selected):not(.react-datepicker__day--in-range) {
    background: rgba(239, 68, 68, 0.12);
  }

  .react-datepicker__navigation {
    top: 8px;
  }
  .react-datepicker__navigation-icon::before {
    border-color: var(--primary-contrast);
  }

  [data-theme='dark'] .react-datepicker {
    background: var(--panel-bg);
  }

  .range-inputs {
    display: flex;
    align-items: center;
    /* Gdy obie daty nie mieszcza sie w jednym wierszu (okno edycji przy 320px), Do przechodzi
       do drugiego wiersza, zamiast ucinac cyfry dnia (przeglad 28.09.2026). */
    flex-wrap: wrap;
    gap: 4px 10px;
    background: var(--input-bg, rgba(255,255,255,0.06));
    border: 2px solid var(--input-border);
    border-radius: 14px;
    padding: 10px 12px; /* ZMIENIONE: mniejszy padding */
    min-height: 44px; /* jeden wiersz: 44px jak dotad; przy dwoch rosnie */
    cursor: pointer;
    transition: border-color .2s, box-shadow .2s, background .2s;
    font-size: 0.95rem;
    width: 100%;
    box-sizing: border-box;
  }

  .range-inputs:focus-within {
    border-color: var(--primary);
    background: var(--card-bg);
    box-shadow: var(--focus-ring);
  }
  .range-inputs input {
    flex: 1;
    /* Bez width 0 pole tekstowe trzyma wrodzona szerokosc (20 znakow, ok. 166px), wiec rzad Od-Do
       mial co najmniej 392px i rozpychal formularz na telefonie (przeglad galezi, 28.09.2026).
       Dolna granica to sama data: 8 cyfr po 1ch (cyfry o stalej szerokosci) i 2 laczniki, czyli
       9.2ch w Arialu (cyfra 0.556em, lacznik 0.333em) i ok. 9.1ch w czcionce systemowej; mniej
       ucinalo dzien („2026-09-2”), wiec wtedy rzad sie zawija. Wiecej niz 9.2ch zawijalo rzad na
       iPhonie 375px, gdzie na pole zostaje 91px (pomiar z czcionka systemowa). */
    min-width: 9.2ch;
    width: 0;
    font-variant-numeric: tabular-nums;
    background: transparent;
    border: 0;
    outline: none;
    font-size: 0.95rem;
    font-weight: 500;
    color: var(--text);
    line-height: 1.2;
    padding: 0;
  }
  /* Regula dotyku w index.css daje polom tekstowym min-height 44px (specyficznosc 0,2,1), a tu
     pole jest tylko tekstem w ramce 44px: bez tego rzad mial 68px (przeglad 28.09.2026, pomiar). */
  .range-inputs.range-inputs input {
    min-height: 0;
  }
  .range-inputs input::placeholder {
    color: var(--text-muted);
    font-weight: 400;
  }
  .range-sep {
    font-size: 0.85rem;
    opacity: 0.65;
    user-select: none;
  }

  .range-inputs.filled {
    background: rgba(59,130,246,0.06);
  }
`;

export const DatepickerZIndexFix = styled.div`
  .react-datepicker-popper {
    z-index: var(--z-popover) !important;
  }
`;

export const EquipmentItemRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  margin-bottom: 8px;
  background: var(--card-bg);
  border-radius: 8px;
  border: 1px solid var(--input-border);
  
  &:last-child { margin-bottom: 0; }
`;

export const RemoveButton = styled.button`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  color: var(--danger);
  padding: 6px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(239, 68, 68, 0.2);
    border-color: rgba(239, 68, 68, 0.4);
    transform: scale(1.1);
  }
`;

export const EquipmentButton = styled(PrimaryButton)`
  background: ${props => props.enabled ? 'var(--success)' : 'var(--text-muted)'};
  cursor: ${props => props.enabled ? 'pointer' : 'not-allowed'};
  padding: 8px 16px;
  font-size: 0.9rem;
`;

export const FormModal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-modal);
  backdrop-filter: blur(8px);
  overflow-y: auto;
  padding: 20px;
`;

export const FormContainer = styled.div`
  background: var(--card-bg);
  border-radius: 20px;
  padding: 32px;
  max-width: 1000px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 75px var(--shadow-color);
  border: 1px solid var(--input-border);
  position: relative; /* DODANE: dla pozycjonowania przycisku X */
  
  @media(max-width: 768px) {
    padding: 20px;
  }
`;

export const FormHeader = styled.div`
  margin-bottom: 32px;
  padding-bottom: 20px;
  border-bottom: 2px solid var(--input-border);
`;

export const FormTitle = styled.h2`
  margin: 0 0 8px 0;
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 12px;
  
  @media(max-width: 768px) {
    font-size: 1.5rem;
  }
`;

export const EquipmentActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
`;

export const FormSubtitle = styled.p`
  margin: 0;
  font-size: 0.95rem;
  color: var(--text-muted);
`;

export const FormPanel = styled.div`
  background: var(--panel-bg);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  border: 1px solid var(--input-border);
  
  &:last-child {
    margin-bottom: 0;
  }
`;

export const FormPanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--input-border);
`;

// Wspólna plakietka ikony z ui/common.js (IconBadge) — wołający przekazuje `$tone="steel"` itp.
// zamiast `bg="var(--primary)"`, patrz komentarz przy ICON_TONES w ui/common.js.
export const FormPanelIcon = IconBadge;

export const FormPanelTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text);
`;

export const FormInputGroup = styled.div`
  display: grid;
  /* Kolumny zaleza od szerokosci SAMEJ grupy, nie ekranu. Okno szczegolow rezerwacji na iPadzie
     w pionie jest duzo wezsze od ekranu, a dawny warunek na szerokosc ekranu (720px) zostawial
     tam dwie kolumny i pole rabatu mialo 27px (symulator iOS, 27.09.2026). Najwyzej dwie
     kolumny (kazda co najmniej polowa minus odstep), jedna, gdy na kolumne zostaje < 180px.
     min(100%, ...): kolumna nigdy nie jest szersza od samej grupy, inaczej na iPadzie mini
     i w waskim oknie komputera pola wystawaly poza panel (przeglad galezi, 28.09.2026). 180,
     a nie 220: przy 220 szeroki ekran (grupa ok. 409px) tracil dwie kolumny. */
  grid-template-columns: repeat(auto-fit, minmax(min(100%, max(180px, calc(50% - 8px))), 1fr));
  gap: 16px;
  align-items: start;

  /* Ponizej 720px zawsze jedna kolumna, jak przed ta zmiana. Rodzic grupy liczy jej najmniejsza
     szerokosc tresci, a dla tej siatki wychodzilo ok. 392px (dwie kolumny), wiec na telefonie
     360-412px caly formularz edycji rozjezdzal sie do 442px, pola wystawaly, a kalendarzyk dat
     nie mial gdzie sie zmiescic (zmierzone w Chromium z emulacja dotyku, 28.09.2026). */
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }

  /* Zapobiega rozszerzaniu się wewnętrznych divów poza komórki grida */
  & > div {
    min-width: 0;
  }

  /* większy odstęp między etykietą a polem w trybie edycji */
  & label {
    margin-bottom: 20px;
  }

`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  
  @media(max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  align-items: start;

  & > div { min-width: 0; }

  @media(max-width: 920px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media(max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const FormInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

export const FormLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text);
`;

export const FormValue = styled.div`
  padding: 12px 16px;
  background: var(--input-bg);
  border: 2px solid var(--input-border);
  border-radius: 12px;
  font-size: 1rem;
  color: var(--text);
  min-height: 48px;
  display: flex;
  align-items: center;
  /* Bez tego dlugie slowo (np. "niezablokowana") nie zwezi sie ponizej swojej
     szerokosci i wychodzi poza ramke w waskiej kolumnie. */
  min-width: 0;
  overflow-wrap: anywhere;
  
  &.empty {
    color: var(--text-muted);
    font-style: italic;
  }
`;

export const InputGroup = styled.div`
  /* wymuszony układ w rzędzie (override możliwych nadpisań) */
  display: flex !important;
  flex-direction: row !important;
  gap: 16px;
  align-items: flex-start;
  width: 100%;

  /* Pola dopasowują się do TREŚCI, zamiast dzielić rząd na równe części.
     Wcześniej flex 1 1 0 razem z min-width 0 dawało każdemu dokładnie jedną trzecią
     niezależnie od tego, co w nim jest, a overflow-wrap anywhere z FormValue łamało
     to, co się nie zmieściło, W ŚRODKU SŁOWA: „150.00 zł (zablokowa na na karcie)".
     min-content to szerokość najdłuższego słowa, więc pole nigdy nie zwęzi się
     poniżej niego, a wrap niżej pozwala rzędowi przenieść pole do nowej linii
     zamiast je gnieść. */
  flex-wrap: wrap;

  & > ${FormInputWrapper} {
    flex: 1 1 auto;
    min-width: min-content;
    max-width: none;
  }

  /* upewnij się, że FormValue zajmuje całą dostępną szerokość wewnątrz wrappera */
  & ${FormValue} {
    width: 100%;
    box-sizing: border-box;
    /* Skoro pole ma juz szerokosc najdluzszego slowa, lamanie w srodku wyrazu jest
       zbedne. Zostaje lamanie na spacjach, czyli tam, gdzie czlowiek sie tego spodziewa. */
    min-width: min-content;
    overflow-wrap: normal;
  }

  /* tylko na bardzo wąskich ekranach wracamy do kolumny */
  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

export const CompactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  align-items: stretch;
  margin-bottom: 24px; /* DODANE: odstęp od następnego panelu */

  & > div {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  & > div:nth-child(2) input,
  & > div:nth-child(2) select {
    padding: 10px 12px;
    font-size: 0.95rem;
    min-height: 40px;
  }

  /* Na dotyku 44px (pole „zl” linku na inna kwote mialo 40px, iPad 27.09.2026). */
  @media (max-width: 768px), (hover: none) and (pointer: coarse) {
    & > div:nth-child(2) input,
    & > div:nth-child(2) select {
      min-height: 44px;
    }
  }

  & > div:nth-child(2) ${FormInputGroup} {
    gap: 12px;
  }

  & > div:nth-child(2) ${FormInputWrapper} {
    gap: 6px;
  }

  @media(max-width: 600px) {
    grid-template-columns: 1fr;
    align-items: initial;
    margin-bottom: 16px;
    
    & > div {
      height: auto;
    }
  }
`;

export const FormEquipmentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const SummaryTable = styled.div`
  background: var(--panel-bg);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid var(--input-border);
`;

export const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid var(--input-border);

  &:last-child {
    border-bottom: none;
    background: rgba(59,130,246,0.10);
    font-weight: 700;
    color: var(--text);
  }
`;

export const SummaryLabel = styled.span`
  font-weight: 600;
  color: var(--text);
`;

export const SummaryValue = styled.span`
  font-weight: 500;
  color: var(--text);
`;

export const FormButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid var(--input-border);

  @media (max-width: 768px) {
    flex-direction: column-reverse;
    gap: 10px;
  }
`;

export const DiscountField = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

export const PercentInline = styled.span`
  margin-left: 8px;
  color: var(--text-muted);
  font-weight: 700;
  font-size: 0.95rem;
`;


export const DocFileButton = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 18px;
  border: 2px dashed var(--input-border);
  border-radius: 12px;
  background: var(--input-bg);
  color: var(--text);
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;
  &:hover { border-color: var(--primary); color: var(--primary); }
  input { display: none; }
  /* Zablokowany na czas wysylki dokumentacji (ReservationFormView#addPhotoEntry). */
  ${(p) => (p.$disabled ? 'opacity: 0.6; cursor: not-allowed; pointer-events: none;' : '')}
`;

export const DocEntry = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 10px 12px;
  border: 1px solid var(--input-border);
  border-radius: 12px;
  background: var(--card-bg);
`;

export const DocRemove = styled.button`
  background: none;
  border: none;
  color: var(--danger);
  cursor: pointer;
  font-size: 0.82rem;
  font-weight: 600;
  padding: 4px 6px;
  &:hover { text-decoration: underline; }
`;
